import { useEffect, useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { apiClient, apiUrl, supergreenLanding } from "../../constants.ts";
import {
  Button,
  Collapsible,
  CopyableValue,
  customConfirm,
  inputClass,
  PageSpinner,
} from "./components.tsx";
import { useAuth, useQuery } from "./db.ts";

export const WhatsappPrivateConfig = ({ id }: { id: string }) => {
  const [newWhatsappNumber, setWhatsappNumber] = useState<string>("");
  const [newSecretToken, setSecretToken] = useState<string>("");
  const [addingNew, setAddingNew] = useState(false);
  const token = useAuth().user?.refresh_token;

  const [demoCode, setDemoCode] = useState<string>("");
  const [demoLink, setDemoLink] = useState<string>("");
  const [loadingDemo, setLoadingDemo] = useState<boolean>(true);

  useEffect(() => {
    if (!token) return;
    apiClient({
      token,
      endpoint: "get-whatsapp-demo-info",
      payload: { botId: id },
    })
      .then((res) => {
        if (res.success) {
          setDemoCode(res.code ?? "");
          setDemoLink(res.link ?? "");
        }
      })
      .catch((err) => {
        console.error("Failed to load WhatsApp demo info:", err);
      })
      .finally(() => {
        setLoadingDemo(false);
      });
  }, [id, token]);

  const { data, error, isLoading } = useQuery({
    bots: { $: { where: { id } }, supergreenWaAccounts: {} },
  });

  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error: {error.message}</div>;

  const { bots } = data;
  const [bot] = bots;
  if (!bot) return null;

  const accounts = bot.supergreenWaAccounts ?? [];
  const showAddForm = accounts.length === 0 || addingNew;

  return (
    <Collapsible title="WhatsApp">
      <div className="mb-6 p-4 border border-emerald-200 bg-emerald-50/50 dark:border-emerald-950/40 dark:bg-emerald-950/20 rounded-lg">
        <h3 className="font-semibold text-emerald-950 dark:text-emerald-50 text-sm mb-2 flex items-center gap-2">
          <span>WhatsApp Demo & Testing</span>
          <span className="bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200 text-xs px-2 py-0.5 rounded-full font-normal">
            Instant
          </span>
        </h3>
        <p className="text-xs text-emerald-900/80 dark:text-emerald-100/80 mb-4 leading-relaxed">
          Test your agent on WhatsApp without setting up your own number. Use
          our shared demo number by sending the switch command.
        </p>

        {loadingDemo
          ? (
            <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
              Loading demo info...
            </div>
          )
          : (
            <div className="flex flex-col gap-3 max-w-md">
              <CopyableValue
                label="WhatsApp Demo Switch Code"
                value={`/switch ${demoCode}`}
              />
              {demoLink && (
                <div className="mt-1">
                  <Button
                    onClick={() => {
                      globalThis.open(demoLink, "_blank");
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white border-none py-1.5 px-4 text-xs"
                  >
                    Open WhatsApp Demo Chat
                  </Button>
                </div>
              )}
            </div>
          )}
      </div>

      <div className="border-t border-border-subtle-light dark:border-border-subtle pt-6">
        Set up your number first on{" "}
        <a href={supergreenLanding} target="_blank" rel="noopener noreferrer">
          supergreen
        </a>. You will then get a secret token. We configure your webhook URL
        automatically on Supergreen, but if you need to set it manually, the
        Webhook URL is:
        <div className="mt-2 mb-4 w-full max-w-md">
          <CopyableValue
            label="Webhook URL"
            value={`${apiUrl}/supergreen`}
          />
        </div>
      </div>

      {showAddForm && (
        <div className="mb-4 flex flex-col gap-4 border border-border-subtle-light dark:border-border-subtle rounded-lg p-4 mt-3">
          <h3 className="font-semibold text-sm">Add WhatsApp Number</h3>
          <div>
            <label className="text-xs font-medium block mb-1">
              WhatsApp number
            </label>
            <input
              value={newWhatsappNumber}
              placeholder="e.g. 972501234567"
              onChange={(e) => {
                setWhatsappNumber(e.currentTarget.value);
              }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">
              Secret Token
            </label>
            <input
              type="password"
              value={newSecretToken}
              placeholder="Your supergreen secret token"
              onChange={(e) => {
                setSecretToken(e.currentTarget.value);
              }}
              className={inputClass}
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                if (!token) return;
                toast.promise(
                  apiClient({
                    token,
                    endpoint: "set-whatsapp-number",
                    payload: {
                      whatsappNumber: newWhatsappNumber,
                      botId: bot.id,
                      secretToken: newSecretToken,
                    },
                  }),
                  {
                    loading: "Connecting bot to WhatsApp...",
                    success: (res) => {
                      if (res.success) {
                        setWhatsappNumber("");
                        setSecretToken("");
                        setAddingNew(false);
                        return "Bot connected successfully!";
                      }
                      throw new Error(res.error);
                    },
                    error: (err) => {
                      return `Failed to connect bot: ${err.message}`;
                    },
                  },
                );
              }}
              disabled={!newWhatsappNumber || !newSecretToken}
            >
              Add WhatsApp Number
            </Button>
            {accounts.length > 0 && (
              <Button onClick={() => setAddingNew(false)}>Cancel</Button>
            )}
          </div>
        </div>
      )}

      {accounts.length > 0 && !showAddForm && (
        <div className="flex flex-col gap-4 w-full mt-3">
          <div className="flex justify-between items-center border-b border-border-subtle-light dark:border-border-subtle pb-2 mb-2">
            <span className="text-sm font-semibold">
              Connected Numbers ({accounts.length})
            </span>
            <Button
              onClick={() => setAddingNew(true)}
              className="text-xs py-1 px-3"
            >
              + Add another number
            </Button>
          </div>

          <div className="flex flex-col gap-4">
            {accounts.map((
              account: { id: string; phoneNumber: string; secretToken: string },
            ) => (
              <div
                key={account.id}
                className="bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle rounded-lg p-4 flex flex-col gap-3"
              >
                <div className="flex flex-col md:flex-row gap-4">
                  <CopyableValue
                    label="WhatsApp Number"
                    value={account.phoneNumber}
                  />
                  <CopyableValue
                    label="Secret Token"
                    value={account.secretToken}
                  />
                </div>
                <div className="flex flex-col md:flex-row gap-2">
                  <Button
                    onClick={() => {
                      globalThis.open(
                        `https://wa.me/${account.phoneNumber}`,
                        "_blank",
                      );
                    }}
                    className="w-full md:w-auto"
                  >
                    Talk on WhatsApp
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!token) return;
                      if (
                        !await customConfirm(
                          `Are you sure you want to disconnect WhatsApp account ${account.phoneNumber}?`,
                        )
                      ) {
                        return;
                      }
                      toast.promise(
                        apiClient({
                          token,
                          endpoint: "disconnect-whatsapp",
                          payload: {
                            botId: bot.id,
                            phoneNumber: account.phoneNumber,
                          },
                        }),
                        {
                          loading: "Disconnecting WhatsApp...",
                          success: (res) => {
                            if (res.success) {
                              return "WhatsApp disconnected successfully!";
                            }
                            throw new Error(res.error);
                          },
                          error: (err) =>
                            `Failed to disconnect: ${err.message}`,
                        },
                      );
                    }}
                    className="w-full md:w-auto bg-danger hover:bg-danger-hover"
                  >
                    Disconnect
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Collapsible>
  );
};
